import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

const WSContext = createContext({ subscribe: () => () => {}, connected: false });

export function WebSocketProvider({ children }) {
  const listenersRef = useRef(new Map()); // event -> Set<fn>
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);

  const connect = useCallback(() => {
    const token = localStorage.getItem("sh_token");
    if (!token) return;
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;
    // Build ws url
    const wsUrl = backend.replace(/^http/, "ws") + `/api/ws?token=${encodeURIComponent(token)}`;
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        // Ping every 25s to keep heartbeat
        if (pingTimerRef.current) clearInterval(pingTimerRef.current);
        pingTimerRef.current = setInterval(() => {
          try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
        }, 25000);
      };
      ws.onmessage = (e) => {
        try {
          const { event, data } = JSON.parse(e.data);
          const fns = listenersRef.current.get(event);
          if (fns) fns.forEach(fn => { try { fn(data); } catch {} });
          // wildcard
          const all = listenersRef.current.get("*");
          if (all) all.forEach(fn => { try { fn({ event, data }); } catch {} });
        } catch {}
      };
      ws.onclose = () => {
        setConnected(false);
        if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
        // reconnect in 3s if still have token
        if (localStorage.getItem("sh_token")) {
          reconnectTimerRef.current = setTimeout(connect, 3000);
        }
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    } catch {
      reconnectTimerRef.current = setTimeout(connect, 5000);
    }
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      try { wsRef.current?.close(); } catch {}
    };
  }, [connect]);

  const subscribe = useCallback((event, fn) => {
    if (!listenersRef.current.has(event)) listenersRef.current.set(event, new Set());
    listenersRef.current.get(event).add(fn);
    return () => {
      const s = listenersRef.current.get(event);
      if (s) s.delete(fn);
    };
  }, []);

  return <WSContext.Provider value={{ subscribe, connected }}>{children}</WSContext.Provider>;
}

export const useWS = () => useContext(WSContext);
